export const dashboardSasEnabled = true;

export const dashboardWidgetTemplates = [
  {
    key: 'list.standard',
    label: 'Standard List',
    description: 'Shows the primary fields, three detail fields, first sort field, and 10 rows.',
    widgetTypes: ['List'],
    defaults: {
      listFields: {
        primaryLeft: 'primary',
        primaryRight: 'next',
        detailsCount: 3,
        sort: 'first',
      },
      maxRows: 10,
    },
  },
  {
    key: 'chart.trend',
    label: 'Trend Chart',
    description: 'Shows the first trend group by month for the last 180 days.',
    widgetTypes: ['Chart'],
    defaults: {
      chartGroup: 'trend',
      chartType: 'Line',
      chartTime: { scope: 'last_days', days: 180, valueMode: 'period', groupBy: 'month' },
    },
  },
  {
    key: 'chart.distribution',
    label: 'Distribution Chart',
    description: 'Shows the first distribution group as an all-time doughnut.',
    widgetTypes: ['Chart'],
    defaults: {
      chartGroup: 'distribution',
      chartType: 'Doughnut',
      chartTime: { scope: 'all', valueMode: 'period' },
    },
  },
  {
    key: 'stats.summary',
    label: 'Summary Statistics',
    description: 'Shows the first three available statistics.',
    widgetTypes: ['Stats'],
    defaults: { statsCount: 3 },
  },
  {
    key: 'generic.standard',
    label: 'Generic Widget',
    description: 'Uses the standard setup for the selected generic widget type.',
    widgetTypes: ['Buttons', 'Search', 'Notifications', 'DailyReport'],
    defaults: { buttonCount: 4 },
  },
  {
    key: 'custom',
    label: 'Custom',
    description: 'Keeps the current widget configuration.',
    widgetTypes: ['List', 'Chart', 'Stats', 'Buttons', 'Search', 'Notifications', 'DailyReport'],
    defaults: {},
  },
];

const dashboardChartTimeWindows = [
  'today',
  'yesterday',
  'last_7_days',
  'last_30_days',
  'this_month',
  'last_6_months',
  'ytd',
  'custom',
  'all',
];

const dashboardChartTimeModes = ['window', 'cumulative_since', 'snapshot'];
const dashboardChartGroupBy = ['hour', 'day', 'week', 'month'];
const dashboardChartDateScopes = ['today', 'last_days', 'this_month', 'this_year', 'date_range'];
const dashboardChartValueModes = ['period', 'cumulative'];

const dashboardItemConfig = (router) => {
  const userParam = router.currentRoute.params['user'];

  const ViewAction = (jobnoKey, link) => {
    return {
      label: 'View',
      key: 'view',
      clickFunction: (item) => {
        router.push(`/Home/${userParam}${link}?View=${item[jobnoKey]}`);
      },
    };
  };

  const EditAction = (jobnoKey, link) => ({
    label: 'Edit',
    key: 'edit',
    clickFunction: (item) => {
      router.push(`/Home/${userParam}${link}?Edit=${item[jobnoKey]}`);
    },
  });

  const ViewEditSet = (jobnoKey, link) => [ViewAction(jobnoKey, link), EditAction(jobnoKey, link)];

  const TasUpdate = (jobnoKey) => {
    return {
      label: 'TAS',
      key: 'tas',
      clickFunction: (item) => router.push(`/Home/${userParam}/Status?jobno=${encodeURIComponent(item[jobnoKey])}`),
    };
  };

  const ChartSeries = (key, label, unit = 'jobs', precision = 0) => ({ key, label, unit, precision });
  const ChartGroup = (key, label, series, filters, chartTypes = ['Line', 'Bar'], defaultDateScope = 'six_months') => {
    const distribution = chartTypes.length == 1 && chartTypes[0] == 'Doughnut';
    return {
      key,
      label,
      chartType: distribution ? 'Doughnut' : 'Line',
      chartTypes,
      series,
      filters,
      defaultDateScope,
      dateScopes: distribution ? [...dashboardChartDateScopes, 'all'] : dashboardChartDateScopes,
      defaultScope: distribution ? 'all' : 'last_days',
      defaultDays: distribution ? null : 180,
      valueModes: distribution ? ['period'] : dashboardChartValueModes,
      defaultValueMode: 'period',
      timeModes: distribution ? ['window', 'snapshot'] : dashboardChartTimeModes,
      timeWindows: distribution ? dashboardChartTimeWindows : dashboardChartTimeWindows.filter((item) => item != 'all'),
      groupBy: distribution ? [] : dashboardChartGroupBy,
      defaultTimeMode: distribution ? 'snapshot' : 'window',
      defaultTimeWindow: defaultDateScope == 'none' ? 'all' : defaultDateScope,
      defaultGroupBy: distribution ? null : 'month',
    };
  };

  const LegacyChartMapping = (dataToShow, group, chartTypes = null, targetChartType = null) => ({
    dataToShow,
    group,
    chartTypes,
    targetChartType,
  });

  const freightLegacyChartMappings = [
    LegacyChartMapping('customsstatus', 'customsstatusdistribution', ['Doughnut'], 'Doughnut'),
    LegacyChartMapping('customsstatus', 'customsstatus'),
    LegacyChartMapping('failuremeasure', 'customsfailuresdistribution', ['Doughnut'], 'Doughnut'),
    LegacyChartMapping('failuremeasure', 'customsfailures', null, 'Bar'),
    LegacyChartMapping('jobcount', 'jobvolume'),
    LegacyChartMapping('cumulativejobcount', 'jobvolume', null, 'Line'),
  ];

  const truckingLegacyChartMappings = [
    LegacyChartMapping('jobassigned', 'jobassignment'),
    LegacyChartMapping('jobassigned', 'jobassignmentdistribution', ['Doughnut'], 'Doughnut'),
    LegacyChartMapping('jobcount', 'jobvolume'),
    LegacyChartMapping('resolvedarea', 'areadistribution', null, 'Doughnut'),
    LegacyChartMapping('destination', 'destinationdistribution', null, 'Doughnut'),
  ];

  const haulageLegacyChartMappings = [
    LegacyChartMapping('jobstate', 'jobstatus'),
    LegacyChartMapping('jobstate', 'jobstatusdistribution', ['Doughnut'], 'Doughnut'),
    LegacyChartMapping('jobcount', 'jobvolume'),
    LegacyChartMapping('cumulativejobcount', 'jobvolume', null, 'Line'),
  ];

  const dashboard = [
    {
      title: 'Universal Search',
      key: 'search',
      icon: 'https://dsmlib.logistics.systems/Icons/Search.png',
      type: ['Search'],
      controlKey: 'WidgetSearch',
      table: [
        {
          label: 'Haulage',
          key: 'haulage',
          controlKey: 'WidgetSearchHaulage',
          actions: [...ViewEditSet('primaryValue', '/Haulage/Haulage Job')],
        },
        {
          label: 'Trucking Job',
          key: 'trucking',
          controlKey: 'WidgetSearchTrucking',
          actions: [...ViewEditSet('primaryValue', '/Trucking/Trucking Job')],
        },
        {
          label: 'Workshop Job',
          key: 'workshopjob',
          controlKey: 'WidgetSearchWorkshopJob',
          actions: [...ViewEditSet('primaryValue', '/Workshop/WorkshopJob')],
        },
        {
          label: 'Employee',
          key: 'employee',
          controlKey: 'WidgetSearchEmployee',
          actions: [...ViewEditSet('primaryValue', '/MasterFiles/ProfileMaster/Employee')],
        },
        {
          label: 'Vehicle',
          key: 'vehicle',
          controlKey: 'WidgetSearchVehicle',
          actions: [...ViewEditSet('primaryValue', '/MasterFiles/OperationMaster/Vehicle')],
        },
        {
          label: 'Import Jobs',
          key: 'freightimport',
          controlKey: 'WidgetSearchFreightImport',
          actions: [...ViewEditSet('primaryValue', '/FreightForwarding/Import Job'), TasUpdate('primaryValue')],
        },
        {
          label: 'Export Jobs',
          key: 'freightexport',
          controlKey: 'WidgetSearchFreightExport',
          actions: [...ViewEditSet('primaryValue', '/FreightForwarding/Export Job'), TasUpdate('primaryValue')],
        },
      ],
    },
    {
      title: 'Quick Actions',
      key: 'buttons',
      icon: 'https://dsmlib.logistics.systems/Icons/icons8-add-user-group-man-man-64.png',
      type: ['Buttons'],
      controlKey: 'WidgetButtons',
      buttons: [
        {
          label: 'New Import Job',
          key: 'freightImportNew',
          link: '/FreightForwarding/Import Job?New',
          controlKey: 'WidgetButtonFreightImportNew',
        },
        {
          label: 'New Export Job',
          key: 'freightExportNew',
          link: '/FreightForwarding/Export Job?New',
          controlKey: 'WidgetButtonFreightExportNew',
        },
        {
          label: 'New Delivery Note',
          key: 'freightDeliveryNoteNew',
          link: '/FreightForwarding/Delivery Notes?New',
          controlKey: 'WidgetButtonDeliveryNoteNew',
        },
        { label: 'New ROT', key: 'ROTNew', link: '/FreightForwarding/ROT?New', controlKey: 'WidgetButtonROTNew' },
        {
          label: 'Haulage Job Listing',
          key: 'haulage',
          link: '/Haulage/Haulage Job',
          controlKey: 'WidgetButtonHaulage',
        },
        {
          label: 'New Haulage Job',
          key: 'haulageNew',
          link: '/Haulage/Haulage Job?New',
          controlKey: 'WidgetButtonHaulageNew',
        },
        {
          label: 'Workshop Job Listing',
          key: 'workshopJob',
          link: '/Workshop/WorkshopJob',
          controlKey: 'WidgetButtonWorkshopJob',
        },
        {
          label: 'New Workshop Job',
          key: 'workshopJobNew',
          link: '/Workshop/WorkshopJob?New',
          controlKey: 'WidgetButtonWorkshopJobNew',
        },
        {
          label: 'Part Inventory Listing',
          key: 'partInventory',
          link: '/Workshop/PartInventory',
          controlKey: 'WidgetButtonPartInventory',
        },
        {
          label: 'New Part Inventory',
          key: 'partInventoryNew',
          link: '/Workshop/PartInventory?New',
          controlKey: 'WidgetButtonPartInventoryNew',
        },
      ],
    },
    {
      title: 'Notifications (Preview)',
      key: 'noficiations',
      icon: 'https://dsmlib.logistics.systems/Icons/icons8-notification-64.png',
      type: ['Notifications'],
      disabled: true,
      controlKey: 'WidgetNotifications',
      moduleMapping: [
        { label: '', key: 'Haulage', link: '/Haulage/Haulage Job' },
        { label: '', key: 'WorkshopJob', link: '/Workshop/WorkshopJob' },
        { label: 'Freight Forwarding - Import Jobs', key: 'FreightImport', link: '/FrerightForwarding/Import Job' },
      ],
    },
    {
      title: 'Vehicle',
      key: 'vehicle',
      link: '/MasterFiles/OperationMaster/Vehicle',
      type: ['List'],
      controlKey: 'WidgetVehicle',
      optionsMode: true,
      fields: [
        { label: 'Vehicle #', key: 'vehicleno', primary: true },
        { label: 'Vehicle Code', key: 'vehiclecode' },
        { label: 'Category', key: 'category' },
        { label: 'Type', key: 'type' },
        { label: 'Availability', key: 'availability' },
        { label: 'Remarks', key: 'remarks', span: 2 },
        { label: 'Team', key: 'team' },
        { label: 'Sideloader', key: 'sideloader' },
        { label: 'Meter Reading', key: 'meterreading' },
        { label: 'Last Update', key: 'lastupdate' },
        { label: 'Updated By', key: 'updatedby' },
      ],
      sortFields: [
        { label: 'Vehicle Code', key: 'vehiclecode', defaultDesc: false },
        { label: 'Vehicle #', key: 'vehicleno', defaultDesc: false },
        { label: 'Team', key: 'team', defaultDesc: false },
        { label: 'Type', key: 'type', defaultDesc: false },
        { label: 'Availability', key: 'availability', defaultDesc: false },
        { label: 'Date Updated', key: 'updatedlatest', defaultDesc: true },
      ],
      fieldsActions: [...ViewEditSet('primaryValue', '/MasterFiles/OperationMaster/Vehicle')],
      listFilter: [
        {
          label: 'Team',
          key: 'team',
          filterType: 'multiselect',
        },
        {
          label: 'Vehicle Type',
          key: 'vehicletype',
          filterType: 'multiselect',
        },
        {
          label: 'Availability',
          key: 'availability',
          filterType: 'multiselect',
        },
        {
          label: 'No Queued Return After Destination',
          key: 'noqueuedreturnafterdestination',
          filterType: 'boolean',
        },
      ],
    },
    {
      title: 'Employee',
      key: 'employee',
      link: '/MasterFiles/ProfileMaster/Employee',
      type: ['List'],
      controlKey: 'WidgetEmployee',
      optionsMode: true,
      fields: [
        { label: 'Employee No.', key: 'employeeno', primary: true },
        { label: 'Name', key: 'name' },
        { label: 'Status', key: 'status' },
        { label: 'Department', key: 'department' },
        { label: 'Position', key: 'position' },
        { label: 'Branch', key: 'branch' },
        { label: 'Mobile', key: 'mobile' },
        { label: 'Email', key: 'email', span: 2 },
        { label: 'Last Update', key: 'lastupdate' },
        { label: 'Updated By', key: 'updatedby' },
      ],
      sortFields: [
        { label: 'Date Updated', key: 'updatedlatest', defaultDesc: true },
        { label: 'Name', key: 'name', defaultDesc: false },
      ],
      // fieldsActions: [...ViewEditSet('primaryValue', '/MasterFiles/ProfileMaster/Employee')],

      listFilter: [
        {
          label: 'Status',
          key: 'status',
          filterType: 'multiselect',
        },
        {
          label: 'Department',
          key: 'department',
          filterType: 'multiselect',
        },
      ],
    },
    {
      title: 'Import Job',
      key: 'freightimport',
      link: '/FreightForwarding/Import Job',
      type: ['List', 'Chart', 'Stats', 'DailyReport'],
      controlKey: 'WidgetFreightImport',
      optionsMode: true,
      fields: [
        { label: 'Job #', key: 'jobno', primary: true, sort: true },
        { label: 'Job Date', key: 'jobdatetime', sort: true },
        { label: 'Job Status', key: 'jobstate' },
        { label: 'Shipper', key: 'shipper', span: 2 },
        { label: 'Consignee', key: 'consignee', span: 2 },
        { label: 'Billing Customer', key: 'billingcustomer', span: 2 },
        { label: 'BL/Bkg Ref #', key: 'blbkgrefno' },
        { label: 'Cust. Ref #', key: 'custrefno' },
        { label: 'ETA', key: 'eta', sort: true },
        { label: 'Closing Time', key: 'closingtime', sort: true },
        { label: 'Vessel Name', key: 'vesselname', span: 2 },
        { label: 'Voy #', key: 'voyno' },
        { label: 'Port of Discharge', key: 'portofdischarge' },
        { label: 'Total Cont', key: 'totalcont' },
        { label: 'Project #', key: 'projectno' },
        { label: 'Remarks', key: 'remarks', span: 2 },
      ],
      fieldsActions: [...ViewEditSet('primaryValue', '/FreightForwarding/Import Job'), TasUpdate('primaryValue')],

      listFilter: [
        {
          label: 'Special Filters',
          key: 'specials',
          filterType: 'dropdown_list',
        },
        {
          label: 'Job Date Since',
          key: 'datesince',
          filterType: 'dropdown_list',
        },
        {
          label: 'Has Been Sent to Operations (1253A)',
          key: 'has1253a',
          filterType: 'boolean',
        },
        {
          label: 'Include Incorrect Status Updates',
          key: 'includeincorrectstatusupdates',
          filterType: 'boolean',
        },
        {
          label: 'Job #',
          key: 'jobno',
          filterType: 'string_value',
        },
        {
          label: 'Job Date',
          key: 'jobdatetime',
          filterType: 'date_range',
          showTime: false,
        },
        {
          label: 'ETA',
          key: 'eta',
          filterType: 'date_range',
          showTime: false,
        },
        {
          label: 'Closing Time',
          key: 'closingtime',
          filterType: 'date_range',
          showTime: false,
        },
        {
          label: 'Billing Customer',
          key: 'billingcustomer',
          filterType: 'multiselect',
        },
      ],

      stats: [
        { label: 'Total Jobs', key: 'importjobcount' },
        { label: 'Total Jobs Submitted to Customs', key: 'importjobsubmittedcustom' },
        { label: 'Total Paper Passed', key: 'totalpaperpass' },
        { label: 'Total Customs Jobs Released After Paper Pass', key: 'totalreleasedafterpaperpass' },
        {
          label: 'Total Customs Jobs Released After Submission to Customs',
          key: 'totalreleasedofsubmitted',
        },
        { label: 'Total Jobs Submitted, Approved & Released', key: 'totalcompletedoftotal' },
        { label: 'Total Jobs Not Submitted to Customs', key: 'importjobnotsubmitted' },
        {
          label: 'Total Jobs Not Received Released Date After Paper Pass',
          key: 'importjobpaperpassnotreeleased',
        },
      ],

      statsFilter: [
        {
          label: 'Job Date Since',
          key: 'datesince',
          filterType: 'dropdown_list',
        },
        {
          label: 'Has Been Sent to Operations (1253A)',
          key: 'has1253a',
          filterType: 'boolean',
        },
        {
          label: 'Include Incorrect Status Updates',
          key: 'includeincorrectstatusupdates',
          filterType: 'boolean',
        },
        {
          label: 'Job Date',
          key: 'jobdatetime',
          filterType: 'date_range',
          showTime: false,
        },
        {
          label: 'Billing Customer',
          key: 'billingcustomer',
          filterType: 'multiselect',
        },
      ],

      dailyReportFilter: [
        {
          label: 'Cutoff Date',
          key: 'reportdate',
          filterType: 'date_range',
          showTime: false,
        },
        {
          label: 'Include Not Sent To Operations (1253A)?',
          key: 'has1253a',
          filterType: 'boolean',
        },
        {
          label: 'Include Jobs Released More Than 2 Days Before End Date?',
          key: 'includereleasedbeforecutoff',
          filterType: 'boolean',
        },
      ],

      chartFilter: [
        { label: 'Special Filters', key: 'specials', filterType: 'dropdown_list' },
        { label: 'Job Date Since', key: 'datesince', filterType: 'dropdown_list' },
        { label: 'Has Been Sent to Operations (1253A)', key: 'has1253a', filterType: 'boolean' },
        {
          label: 'Job Date',
          key: 'jobdatetime',
          filterType: 'date_range',
          showTime: false,
        },
        {
          label: 'Billing Customer',
          key: 'billingcustomer',
          filterType: 'multiselect',
        },
      ],

      chartGroups: [
        ChartGroup(
          'jobvolume',
          'Job Count',
          [ChartSeries('jobcount', 'Job Count'), ChartSeries('cumulativejobcount', 'Cumulative Job Count')],
          ['specials', 'has1253a', 'jobdatetime', 'billingcustomer'],
        ),
        ChartGroup(
          'customsstatus',
          'Customs Status',
          [
            ChartSeries('customs_totaljobs', 'Total Jobs'),
            ChartSeries('customs_submitted', 'Submitted to Customs'),
            ChartSeries('customs_paperpassed', 'Paper Passed'),
            ChartSeries('customs_releasedafterpaper', 'Released After Paper Pass'),
            ChartSeries('customs_submittedapprovedreleased', 'Submitted, Approved & Released'),
          ],
          ['specials', 'has1253a', 'jobdatetime', 'billingcustomer'],
        ),
        ChartGroup(
          'customsstatusdistribution',
          'Customs Status Distribution',
          [ChartSeries('customsstatusdistribution', 'Customs Status Distribution')],
          ['specials', 'has1253a', 'jobdatetime', 'billingcustomer'],
          ['Doughnut'],
          'none',
        ),
        ChartGroup(
          'customsfailures',
          'Customs Failures',
          [
            ChartSeries('failure_measureok', 'Measure OK'),
            ChartSeries('failure_notsubmitted', 'Not Submitted to Customs'),
            ChartSeries('failure_paperpassednotreleased', 'Paper Passed Not Released'),
          ],
          ['specials', 'has1253a', 'jobdatetime', 'billingcustomer'],
        ),
        ChartGroup(
          'customsfailuresdistribution',
          'Customs Failure Distribution',
          [ChartSeries('failuremeasuredistribution', 'Customs Failure Distribution')],
          ['specials', 'has1253a', 'jobdatetime', 'billingcustomer'],
          ['Doughnut'],
          'none',
        ),
      ],
      legacyChartMappings: freightLegacyChartMappings,

      data: [
        { label: 'Customs Status', key: 'customsstatus', type: [{ key: 'Bar' }, { key: 'Doughnut' }] },
        {
          label: 'Failure To Measures',
          key: 'failuremeasure',
          type: [{ key: 'Bar' }, { key: 'Doughnut', options: { legendPosition: 'right' } }],
        },
        { label: 'Job Count', key: 'jobcount', type: [{ key: 'Line' }, { key: 'Bar' }] },
        { label: 'Cumulative Job Count', key: 'cumulativejobcount', type: [{ key: 'Line' }] },
      ],
    },
    {
      title: 'Export Job',
      key: 'freightexport',
      link: '/FreightForwarding/Export Job',
      type: ['List', 'Chart', 'Stats'],
      controlKey: 'WidgetFreightExport',
      optionsMode: true,
      fields: [
        { label: 'Job #', key: 'jobno', primary: true, sort: true },
        { label: 'Job Date', key: 'jobdatetime', sort: true },
        { label: 'Job Status', key: 'jobstate' },
        { label: 'Shipper', key: 'shipper', span: 2 },
        { label: 'Consignee', key: 'consignee', span: 2 },
        { label: 'Billing Customer', key: 'billingcustomer', span: 2 },
        { label: 'BL/Bkg Ref #', key: 'blbkgrefno' },
        { label: 'Cust. Ref #', key: 'custrefno' },
        { label: 'ETA', key: 'eta', sort: true },
        { label: 'Closing Time', key: 'closingtime', sort: true },
        { label: 'Vessel Name', key: 'vesselname', span: 2 },
        { label: 'Voy #', key: 'voyno' },
        { label: 'Port of Discharge', key: 'portofdischarge' },
        { label: 'Total Cont', key: 'totalcont' },
        { label: 'Project #', key: 'projectno' },
        { label: 'Remarks', key: 'remarks', span: 2 },
      ],
      fieldsActions: [...ViewEditSet('primaryValue', '/FreightForwarding/Export Job'), TasUpdate('primaryValue')],

      listFilter: [
        {
          label: 'Special Filters',
          key: 'specials',
          filterType: 'dropdown_list',
        },
        {
          label: 'Job #',
          key: 'jobno',
          filterType: 'string_value',
        },
        {
          label: 'Job Date',
          key: 'jobdatetime',
          filterType: 'date_range',
          showTime: false,
        },
        {
          label: 'ETA',
          key: 'eta',
          filterType: 'date_range',
          showTime: false,
        },
        {
          label: 'Closing Time',
          key: 'closingtime',
          filterType: 'date_range',
          showTime: false,
        },
        {
          label: 'Billing Customer',
          key: 'billingcustomer',
          filterType: 'multiselect',
        },
      ],

      stats: [
        { label: 'Total Jobs', key: 'importjobcount' },
        { label: 'Total Jobs Submitted to Customs', key: 'importjobsubmittedcustom' },
        { label: 'Total Paper Passed', key: 'totalpaperpass' },
        { label: 'Total Customs Jobs Released After Paper Pass', key: 'totalreleasedafterpaperpass' },
        {
          label: 'Total Customs Jobs Released After Submission to Customs',
          key: 'totalreleasedofsubmitted',
        },
        { label: 'Total Jobs Submitted, Approved & Released', key: 'totalcompletedoftotal' },
        { label: 'Total Jobs Not Submitted to Customs', key: 'importjobnotsubmitted' },
        {
          label: 'Total Jobs Not Received Released Date After Paper Pass',
          key: 'importjobpaperpassnotreeleased',
        },
      ],

      statsFilter: [
        { label: 'Job Date', key: 'jobdatetime', filterType: 'date_range', showTime: false },
        {
          label: 'Job Date Since',
          key: 'datesince',
          filterType: 'dropdown_list',
        },
        {
          label: 'Billing Customer',
          key: 'billingcustomer',
          filterType: 'multiselect',
        },
      ],

      chartFilter: [
        { label: 'Special Filters', key: 'specials', filterType: 'dropdown_list' },
        { label: 'Job Date Since', key: 'datesince', filterType: 'dropdown_list' },
        {
          label: 'Job Date',
          key: 'jobdatetime',
          filterType: 'date_range',
          showTime: false,
        },
        {
          label: 'Billing Customer',
          key: 'billingcustomer',
          filterType: 'multiselect',
        },
      ],

      chartGroups: [
        ChartGroup(
          'jobvolume',
          'Job Count',
          [ChartSeries('jobcount', 'Job Count'), ChartSeries('cumulativejobcount', 'Cumulative Job Count')],
          ['specials', 'jobdatetime', 'billingcustomer'],
        ),
        ChartGroup(
          'customsstatus',
          'Customs Status',
          [
            ChartSeries('customs_totaljobs', 'Total Jobs'),
            ChartSeries('customs_submitted', 'Submitted to Customs'),
            ChartSeries('customs_paperpassed', 'Paper Passed'),
            ChartSeries('customs_releasedafterpaper', 'Released After Paper Pass'),
            ChartSeries('customs_submittedapprovedreleased', 'Submitted, Approved & Released'),
          ],
          ['specials', 'jobdatetime', 'billingcustomer'],
        ),
        ChartGroup(
          'customsstatusdistribution',
          'Customs Status Distribution',
          [ChartSeries('customsstatusdistribution', 'Customs Status Distribution')],
          ['specials', 'jobdatetime', 'billingcustomer'],
          ['Doughnut'],
          'none',
        ),
        ChartGroup(
          'customsfailures',
          'Customs Failures',
          [
            ChartSeries('failure_measureok', 'Measure OK'),
            ChartSeries('failure_notsubmitted', 'Not Submitted to Customs'),
            ChartSeries('failure_paperpassednotreleased', 'Paper Passed Not Released'),
          ],
          ['specials', 'jobdatetime', 'billingcustomer'],
        ),
        ChartGroup(
          'customsfailuresdistribution',
          'Customs Failure Distribution',
          [ChartSeries('failuremeasuredistribution', 'Customs Failure Distribution')],
          ['specials', 'jobdatetime', 'billingcustomer'],
          ['Doughnut'],
          'none',
        ),
      ],
      legacyChartMappings: freightLegacyChartMappings,

      data: [
        { label: 'Customs Status', key: 'customsstatus', type: [{ key: 'Bar' }, { key: 'Doughnut' }] },
        {
          label: 'Failure To Measures',
          key: 'failuremeasure',
          type: [{ key: 'Bar' }, { key: 'Doughnut', options: { legendPosition: 'right' } }],
        },
        { label: 'Job Count', key: 'jobcount', type: [{ key: 'Line' }, { key: 'Bar' }] },
        { label: 'Cumulative Job Count', key: 'cumulativejobcount', type: [{ key: 'Line' }] },
      ],
    },

    {
      title: 'Trucking Jobs',
      key: 'truckingjob',
      link: '/Trucking/Trucking Job',
      type: ['List', 'Chart'],
      controlKey: 'WidgetTruckingJob',
      optionsMode: true,
      fields: [
        { label: 'Job #', key: 'jobno', primary: true, sort: true },
        { label: 'Job Date', key: 'jobdatetime', sort: true },
        { label: 'Job Status', key: 'jobstate' },
        { label: 'Customer', key: 'customer', span: 2 },
        { label: 'Billing Customer', key: 'billingcustomer', span: 2 },
        { label: 'Consignment #', key: 'consignmentno' },
        { label: 'DO #', key: 'dono' },
        { label: 'Project #', key: 'projectno' },
        { label: 'Cust. Ref #', key: 'custrefno' },
        { label: 'Required Date', key: 'requireddate', sort: true },
        { label: 'Pickup Location', key: 'pickuplocation', span: 2 },
        { label: 'Dropoff Location', key: 'dropofflocation', span: 2 },
        { label: 'Destination Code', key: 'destinationcode' },
        { label: 'Destination', key: 'destination', span: 2 },
        { label: 'Opr Tariff Area', key: 'oprtariffarea' },
        { label: 'Tariff Area', key: 'tariffarea' },
        { label: 'Resolved Area', key: 'resolvedarea' },
        { label: 'POD ETA', key: 'podeta', sort: true },
        { label: 'Days Pending', key: 'dayspending', sort: true },
        { label: 'Lorry #', key: 'lorryno' },
        { label: 'Driver', key: 'driver' },
        { label: 'Total Consignments', key: 'totalconsignments' },
        { label: 'Job Assigned', key: 'jobassigned' },
        { label: 'Last Update', key: 'lastupdate' },
        { label: 'Updated By', key: 'updatedby' },
      ],
      sortFields: [
        { label: 'Job #', key: 'jobno', defaultDesc: false },
        { label: 'Job Date', key: 'jobdatetime', defaultDesc: true },
        { label: 'Required Date', key: 'requireddate', defaultDesc: true },
        { label: 'Days Pending', key: 'dayspending', defaultDesc: true },
        { label: 'Destination', key: 'destination', defaultDesc: false },
        { label: 'Resolved Area', key: 'resolvedarea', defaultDesc: false },
        { label: 'POD ETA', key: 'podeta', defaultDesc: true },
        { label: 'Date Updated', key: 'updatedlatest', defaultDesc: true },
      ],
      fieldsActions: [...ViewEditSet('primaryValue', '/Trucking/Trucking Job')],
      listFilter: [
        {
          label: 'Special Filters',
          key: 'specials',
          filterType: 'dropdown_list',
          alwaysShow: true,
        },
        { label: 'Job #', key: 'jobno', filterType: 'string_value' },
        { label: 'Job Date', key: 'jobdatetime', filterType: 'date_range', showTime: false },
        { label: 'Cust. Ref #', key: 'custrefno', filterType: 'string_value' },
        { label: 'DO #', key: 'dono', filterType: 'string_value' },
        { label: 'Billing Customer', key: 'billingcustomer', filterType: 'multiselect' },
        { label: 'POD Pending Over Days', key: 'poddayspending', filterType: 'number_value', validator: 'positiveInteger' },
        { label: 'Destination Area', key: 'area', filterType: 'multiselect' },
      ],
      chartFilter: [
        {
          label: 'Special Filters',
          key: 'specials',
          filterType: 'dropdown_list',
          alwaysShow: true,
        },
        { label: 'Job Date', key: 'jobdatetime', filterType: 'date_range', showTime: false },
        { label: 'Billing Customer', key: 'billingcustomer', filterType: 'multiselect' },
        { label: 'Job Assigned', key: 'jobassigned', filterType: 'multiselect' },
        { label: 'Destination Area', key: 'area', filterType: 'multiselect' },
      ],
      chartGroups: [
        ChartGroup(
          'jobvolume',
          'Job Count',
          [ChartSeries('jobcount', 'Job Count'), ChartSeries('cumulativejobcount', 'Cumulative Job Count')],
          ['specials', 'jobdatetime', 'billingcustomer', 'jobassigned', 'area'],
        ),
        ChartGroup(
          'jobassignment',
          'Assignment Status Trend',
          [ChartSeries('assignment_assigned', 'Assigned'), ChartSeries('assignment_notassigned', 'Not Assigned')],
          ['specials', 'jobdatetime', 'billingcustomer', 'jobassigned', 'area'],
        ),
        ChartGroup(
          'jobassignmentdistribution',
          'Assignment Status',
          [ChartSeries('assignmentdistribution', 'Assignment Status')],
          ['specials', 'jobdatetime', 'billingcustomer', 'jobassigned', 'area'],
          ['Doughnut'],
          'none',
        ),
        ChartGroup(
          'destinationdistribution',
          'Destination',
          [ChartSeries('destinationdistribution', 'Destination')],
          ['specials', 'jobdatetime', 'billingcustomer', 'jobassigned', 'area'],
          ['Doughnut'],
          'none',
        ),
        ChartGroup(
          'areadistribution',
          'Destination Area',
          [ChartSeries('areadistribution', 'Destination Area')],
          ['specials', 'jobdatetime', 'billingcustomer', 'jobassigned', 'area'],
          ['Doughnut'],
          'none',
        ),
      ],
      legacyChartMappings: truckingLegacyChartMappings,
      data: [
        { label: 'Assignment Status', key: 'jobassigned', type: [{ key: 'Doughnut' }, { key: 'Bar' }] },
        { label: 'Job Count', key: 'jobcount', type: [{ key: 'Line' }, { key: 'Bar' }] },
        { label: 'Resolved Area', key: 'resolvedarea', type: [{ key: 'Bar' }, { key: 'Doughnut' }] },
        { label: 'Destination', key: 'destination', type: [{ key: 'Bar' }] },
      ],
    },

    {
      title: 'Haulage',
      key: 'haulage',
      link: '/Haulage/Haulage Job',
      type: ['List', 'Chart'],
      controlKey: 'WidgetHaulage',
      optionsMode: true,
      fields: [
        { label: 'Job No', key: 'jobno', sort: true, primary: true },
        { label: 'Request No', key: 'requestno' },
        { label: 'Job Status', key: 'jobstate' },
        { label: 'Job Date', key: 'jobdatetime', sort: true },
        { label: 'Shipper', key: 'shipper', span: 2 },
        { label: 'Billing Customer', key: 'billingcustomer', span: 2 },
        { label: 'Send To Delivery Address', key: 'sendtodeliveryaddress', span: 2 },
        { label: 'Destination', key: 'destination', span: 2 },
        { label: 'Resolved Area', key: 'resolvedarea' },
        { label: 'Cust Ref No', key: 'custrefno' },
        { label: 'Inco Term', key: 'incoterm' },
        { label: 'Closing Time', key: 'closingtime' },
        { label: 'ETA', key: 'eta' },
        { label: 'BL/Bkg Ref No', key: 'blbkgrefno' },
        { label: 'Shipping Agent', key: 'shippingagent', span: 2 },
        { label: 'Ship Call No', key: 'shipcallno' },
        { label: 'Voy No', key: 'voyno' },
        { label: 'Vessel Name', key: 'vesselname', span: 2 },
        { label: 'Vessel ID', key: 'vesselid' },
        { label: 'Port Of Loading', key: 'portofloading' },
        { label: 'Station', key: 'station' },
        { label: 'Port Of Discharge', key: 'portofdischarge' },
        { label: 'Goods Desc', key: 'goodsdesc', span: 2 },
        { label: 'Packages', key: 'packages' },
        { label: 'KGM', key: 'kgm' },
        { label: 'M3', key: 'm3' },
        { label: 'Chargeable KGM', key: 'chargeablekgm' },
        { label: 'Remarks', key: 'remarks', span: 2 },
        { label: 'Consignee', key: 'consignee', span: 2 },
        { label: 'Shipping Line', key: 'shippingline', span: 2 },
        { label: 'Manifest No', key: 'manifestno' },
        { label: 'Warehouse', key: 'warehouse' },
        { label: 'Job Type', key: 'jobtype' },
        { label: 'Job Assigned', key: 'jobassigned' },
        { label: 'Total Cont', key: 'totalcont' },
        { label: 'Inv/DN Printed', key: 'invdnprinted' },
        { label: 'Project No', key: 'projectno' },
        { label: 'Pass Pend. >2d', key: 'gatepasspending' },
        { label: 'Longest Pend. Pass', key: 'gatepasoldesteta' },
      ],

      listFilter: [
        {
          label: 'Special Filters',
          key: 'specials',
          filterType: 'dropdown_list',
        },
        {
          label: 'Job #',
          key: 'jobno',
          filterType: 'string_value',
        },
        {
          label: 'Cust. Ref #',
          key: 'custrefno',
          filterType: 'string_value',
        },
        {
          label: 'Billing Customer',
          key: 'billingcustomer',
          filterType: 'multiselect',
        },
        {
          label: 'Job Date',
          key: 'jobdatetime',
          filterType: 'date_range',
          showTime: false,
        },
        {
          label: 'ETA',
          key: 'eta',
          filterType: 'date_range',
          showTime: false,
        },
        {
          label: 'Closing Time',
          key: 'closingtime',
          filterType: 'date_range',
          showTime: false,
        },
        {
          label: 'Area',
          key: 'area',
          filterType: 'multiselect',
          alwaysShow: true,
        },
      ],

      chartFilter: [
        {
          label: 'Special Filters',
          key: 'specials',
          filterType: 'dropdown_list',
        },
        {
          label: 'Job Date',
          key: 'jobdatetime',
          filterType: 'date_range',
          showTime: false,
        },
        {
          label: 'Billing Customer',
          key: 'billingcustomer',
          filterType: 'multiselect',
        },
        {
          label: 'Job Status',
          key: 'jobstate',
          filterType: 'multiselect',
        },
        {
          label: 'Area',
          key: 'area',
          filterType: 'multiselect',
          alwaysShow: true,
        },
      ],
      chartGroups: [
        ChartGroup(
          'jobvolume',
          'Job Count',
          [ChartSeries('jobcount', 'Job Count'), ChartSeries('cumulativejobcount', 'Cumulative Job Count')],
          ['specials', 'jobdatetime', 'billingcustomer', 'jobstate', 'area'],
        ),
        ChartGroup(
          'jobstatus',
          'Job Status Trend',
          [
            ChartSeries('status_confirmed', 'Confirmed'),
            ChartSeries('status_unconfirmed', 'Unconfirmed'),
            ChartSeries('status_cancelled', 'Cancelled'),
          ],
          ['specials', 'jobdatetime', 'billingcustomer', 'jobstate', 'area'],
        ),
        ChartGroup(
          'jobstatusdistribution',
          'Job Status',
          [ChartSeries('statusdistribution', 'Job Status')],
          ['specials', 'jobdatetime', 'billingcustomer', 'jobstate', 'area'],
          ['Doughnut'],
          'none',
        ),
      ],
      legacyChartMappings: haulageLegacyChartMappings,

      data: [
        { label: 'Job Status', key: 'jobstate', type: [{ key: 'Doughnut' }, { key: 'Bar' }] },
        { label: 'Cumulative Job Count', key: 'cumulativejobcount', type: [{ key: 'Line' }] },
        { label: 'Job Count', key: 'jobcount', type: [{ key: 'Line' }, { key: 'Bar' }] },
        { label: 'Resolved Area', key: 'resolvedarea', type: [{ key: 'Bar' }, { key: 'Doughnut' }] },
        { label: 'Destination', key: 'destination', type: [{ key: 'Bar' }] },
      ],

      fieldsActions: [...ViewEditSet('primaryValue', '/FreightForwarding/Import Job')],
    },
  ];

  return { dashboard };
};

export default dashboardItemConfig;
